import moment from 'moment';
import React from 'react';
import { constants, analyticsHelpers } from 'day8-ui';
import { getGuestDates } from 'day8-react/build/helpers/week';
import {
  WEEK,
  ROUTES,
  COMPANIES,
  CABIN,
  PACKAGES,
  CABIN_WEEK_STATUS,
  IGNORE_CROSS_SELL_DESTINATION_IDS
} from '../constants';
import { getUniqueEntriesFromArray, capitalizeFirstLetter } from './utilities';
import { getQuery, updateSearch } from './querystring';
import { calculateRatio } from './crew';
import {
  calculateMinFromPriceOnWeek,
  getCabinPriceOnWeekPerPerson,
  getLeadPackagePricePerPerson,
  getSelectedExtrasCost
} from './pricing';
import { getCategoryList } from './category';
import history from '../history';
import { getPackageColorVariable } from './ratings';
import { isCabinBooking } from './booking';

const { FEATURED_WEEKS, TYW_DESTINATION } = constants;
export const getSelectedParams = location => {
  const query = getQuery(location);
  const {
    dest, category, year, boys, girls, hostess, min, max, weekId, paymentOption
  } = query;
  let { weeks, cabinType, soloType, extras } = query;
  // if 'weeks' is not an array, make it an array
  if (!Array.isArray(weeks)) {
    weeks = weeks ? [weeks] : [];
  }
  if (!Array.isArray(cabinType)) {
    cabinType = cabinType ? [cabinType] : [];
  }
  if (!Array.isArray(soloType)) {
    soloType = soloType ? [soloType] : [];
  }
  if (!Array.isArray(extras)) {
    extras = extras ? [extras] : [];
  }

  const weeksNumbers = weeks.map(weekNumber => parseInt(weekNumber, 10));

  return {
    dest: dest && parseInt(dest, 10),
    category: category && parseInt(category, 10),
    weeks: weeksNumbers,
    year: year && parseInt(year, 10),
    cabinType,
    soloType,
    extras: extras.map(extra => parseInt(extra, 10)),
    boys,
    girls,
    hostess,
    min: min && parseInt(min, 10),
    max: max && parseInt(max, 10),
    weekId,
    paymentOption
  };
};
export const isUltraWeek = (week, destinationId) =>
  week &&
  WEEK.ULTRA_WEEKS[week.year] &&
  WEEK.ULTRA_WEEKS[week.year].includes(week.week) &&
  [TYW_DESTINATION.CROATIA_ORIGINAL, TYW_DESTINATION.CROATIA_ULTRA].includes(
    destinationId
  );

// * 2021 => Only Week 27 CR-U has Friday tickets included:
export const isUltraFriday = (week, destinationId) =>
  week &&
  isUltraWeek(week, destinationId) &&
  week.week === WEEK.ULTRA[week.year].ULTRA_FRIDAY &&
  destinationId === TYW_DESTINATION.CROATIA_ULTRA;

export const isPoweredBy = destinationId =>
  [
    TYW_DESTINATION.YACHTNIK_MART_ST_LUCIA,
    TYW_DESTINATION.YACHTNIK_CROATIA,
    TYW_DESTINATION.BVI_CNCT
  ].includes(destinationId);

// * 2021 => Only Week 28 CR-U has Weekend tickets included:
export const isUltraWeekend = (week, destinationId) =>
  week &&
  isUltraWeek(week, destinationId) &&
  week.week === WEEK.ULTRA[week.year].ULTRA_WEEKEND &&
  destinationId === TYW_DESTINATION.CROATIA_ULTRA;

export const isHideoutWeek = destinationId =>
  TYW_DESTINATION.CROATIA_HIDEOUT === destinationId;

export const isWeekSoldOut = week => week.status === WEEK.SOLD_OUT_STATUS;

export const isWeekComingSoon = week =>
  week.status === WEEK.COMING_SOON_STATUS &&
  week.cabinStatus === WEEK.COMING_SOON_STATUS;

export const isWeekInvisible = week => week.invisible;

export const isWeekHighDemand = (
  yachtCount,
  standardYachts,
  destinations,
  location,
  cabins,
  history,
  pathname,
  week
) => {
  let isHighDemand = false;
  if (isWeekSoldOut(week)) {
    return false;
  } // Don't show pill if sold out or coming soon.
  if (isWeekComingSoon(week)) {
    return false;
  }
  const categoryList = getCategoryList(
    standardYachts,
    destinations,
    location,
    cabins,
    history,
    pathname,
    week
  );
  const categoriesNotSelectable = categoryList.every(
    category =>
      category.isComingSoon || category.isSoldOut || category.isUnavailable
  );
  const isAllCategoriesCabinsOnly = categoryList.every(
    category => category.isCabinsOnly
  );

  if (
    (categoriesNotSelectable && yachtCount < 6) ||
    (!yachtCount && isAllCategoriesCabinsOnly)
  ) {
    isHighDemand = true;
  }
  return isHighDemand;
};

export const countYachtsPerWeek = yachts =>
  yachts.reduce(
    (carry, yacht) => ({
      ...carry,
      [yacht.week.year]: {
        ...carry[yacht.week.year],
        [yacht.week.week]:
          (carry[yacht.week.year] &&
            (carry[yacht.week.year][yacht.week.week] + 1 || 1)) ||
          1
      }
    }),
    {}
  );

export const clearWeekSelection = async (location, history) => {
  history.replace({
    pathname: location.pathname,
    search: updateSearch(location, {
      weeks: undefined,
      year: undefined,
      category: undefined
    })
  });
};

export const clearDestinationSelection = async (location, history) => {
  history.replace({
    pathname: location.pathname,
    search: updateSearch(location, { dest: undefined })
  });
};

export const onWeekSelection = (
  week,
  destination,
  location,
  history,
  pathname,
  filterYachts,
  destinations
) => {
  analyticsHelpers.selectedWeek({
    destination_name: destination.name,
    destination_id: destination.id,
    week: week.week,
    financial_year: week.year
  });
  history.push({
    pathname,
    search: updateSearch(location, {
      dest: destination.id,
      weeks: week.week,
      year: week.year
    })
  });

  if (filterYachts) {
    // here using location from history which was just updated
    filterYachts(destinations, history.location);
  }
};

export const changeCategory = (location, pathname, toCategory) => {
  history.push({
    pathname,
    search: updateSearch(location, { category: toCategory })
  });
};

// Week.year is the *financial* year, which can differ from the calendar year the week actually
// sails in (e.g. Mexico week 50/2027 begins 2026-12-05). Anything user facing - year tabs, year
// filters - has to key off the begin date, and only fall back to the financial year when a slim
// week payload has no begin date. The `year` query param stays the financial year, since that is
// what identifies a week alongside `weeks` (see getWeeksForYear).
export const getWeekCalendarYear = week => {
  if (!week) {
    return null;
  }
  return week.beginDate ? new Date(week.beginDate).getFullYear() : week.year;
};

export const getUniqueYearsFromWeeks = weeks => {
  if (!weeks || !weeks.length) {
    return [];
  }
  return getUniqueEntriesFromArray(Object.values(weeks).map(getWeekCalendarYear));
};

export const getFirstYearFromWeeks = weeks => {
  if (!weeks || !weeks.length) {
    return null;
  }
  return Math.min(...weeks.map(w => w.year));
};

export const numberOfNights = week => {
  const beginDate = moment(getGuestDates(week).beginDate);
  const endDate = moment(getGuestDates(week).endDate);
  return endDate.diff(beginDate, 'days');
};

export const areAllWeeksOnDestinationSoldOutOrComingSoon = weeks =>
  weeks.every(
    week =>
      [WEEK.SOLD_OUT_STATUS, WEEK.COMING_SOON_STATUS].includes(week.status) ||
      week.invisible
  ) && !weeks.some(week => week.cabinStatus === WEEK.OPEN_STATUS);

export const areAllWeeksOnDestinationSoldOut = weeks =>
  weeks.every(
    week => [WEEK.SOLD_OUT_STATUS].includes(week.status) || week.invisible
  ) && !weeks.some(week => week.cabinStatus === WEEK.OPEN_STATUS);

export const areAllWeeksOnDestinationComingSoon = weeks =>
  weeks.every(
    week => [WEEK.COMING_SOON_STATUS].includes(week.status) || week.invisible
  ) && !weeks.some(week => week.cabinStatus === WEEK.OPEN_STATUS);

export const filterWeeksOnDestinationByCompany = (destination, companyId) =>
  destination.weeks.filter(w => w.companyId === companyId);

export const getSelectedDestination = (destinations, location) => {
  const params = getSelectedParams(location);
  if (!destinations) {
    return null;
  }
  return destinations.find(d => d.id === params.dest);
};

export const getWeeksForYear = (destinations, location) => {
  const params = getSelectedParams(location);
  if (!destinations) {
    return [];
  }
  const destination = destinations.find(d => d.id === params.dest);
  if (!destination || !destination.weeks) {
    return [];
  }
  const year = params.year || getFirstYearFromWeeks(destination.weeks);
  return destination.weeks.filter(w => w.year === year);
};

export const getSelectedWeeks = (destinations, location) => {
  const { weeks } = getSelectedParams(location);
  const yearWeeks = getWeeksForYear(destinations, location);
  if (!yearWeeks || yearWeeks.length === 0) {
    return [];
  }
  return yearWeeks.filter(w => weeks.includes(w.week));
};

export const getCategoriesForSelectedWeek = (destinations, location) => {
  const weeks = getSelectedWeeks(destinations, location);
  if (!weeks.length) {
    return null;
  }
  return weeks[0].standardYachtWeeks;
};

export const getAvailableCabinsForWeek = (
  allCabins,
  destinations,
  location,
  selectedWeek
) => {
  const weeks = selectedWeek ? [selectedWeek] : getSelectedWeeks(destinations, location);
  const params = getSelectedParams(location);
  if (!weeks.length || !allCabins) {
    return null;
  }
  const year = params.year || getFirstYearFromWeeks(weeks);
  const destinationId = weeks[0].destination.id;
  const weekNumber = weeks[0].week;
  const cabins =
    allCabins[destinationId] &&
    allCabins[destinationId][year] &&
    allCabins[destinationId][year][weekNumber];
  if (!cabins || !Object.keys(cabins).length) {
    return null;
  }
  return cabins;
};

export const getAvailableCabinsTypes = (allCabins, selectedDestinationIds) => {
  const cabins = [];
  Object.keys(allCabins).filter(destinationId => selectedDestinationIds?.length ? selectedDestinationIds.includes(destinationId) : true).forEach(destinationId => {
    Object.keys(allCabins[destinationId]).forEach(year => {
      Object.keys(allCabins[destinationId][year]).forEach(weekNumber => {
        Object.keys(allCabins[destinationId][year][weekNumber]).forEach(packageId => {
          if (allCabins[destinationId][year][weekNumber][packageId].cabin) {
            Object.keys(allCabins[destinationId][year][weekNumber][packageId].cabin).forEach(i => {
              const { type, cabinType, persons } = allCabins[destinationId][year][weekNumber][packageId].cabin[i];
              const found = cabins.find(cabin => cabin.type === type && cabin.cabinType === cabinType && cabin.persons === persons);
              if (!found) {
                cabins.push(allCabins[destinationId][year][weekNumber][packageId].cabin[i]);
              }
            });
          }

          if (allCabins[destinationId][year][weekNumber][packageId].solo) {
            Object.keys(allCabins[destinationId][year][weekNumber][packageId].solo).forEach(i => {
              const { type, cabinType, persons } = allCabins[destinationId][year][weekNumber][packageId].solo[i];
              const found = cabins.find(cabin => cabin.type === type && cabin.cabinType === cabinType && cabin.persons === persons);
              if (!found) {
                cabins.push(allCabins[destinationId][year][weekNumber][packageId].solo[i]);
              }
            });
          }
        });
      });
    });
  });

  if (cabins.length < 1) {
    return null;
  }
  return cabins;
};

export const getCabinsTypesQueryFromBooking = booking => {
  let query = '';
  if (!isCabinBooking(booking)) {
    return query;
  }
  booking.bookedCabinWeeks?.forEach(bcw => {
    const isSoloCabin = bcw.cabinWeek.cabin?.persons === 1 && bcw.cabinWeek.cabin.cabinType === CABIN.CABIN_TYPE;
    query += `&${bcw.cabinWeek.cabin.cabinType}Type=${bcw.cabinWeek?.cabin?.type}${isSoloCabin ? "Solo" : ""}`;
  });
  return query;
};

export const getAvailableSoloForWeekAndCategory = (
  allCabins,
  destinations,
  location,
  packageId,
  selectedWeek
) => {
  const cabins = getAvailableCabinsForWeek(
    allCabins,
    destinations,
    location,
    selectedWeek
  );
  const solo = cabins && cabins[packageId] && cabins[packageId].solo;
  if (!solo || !Object.keys(solo).length) {
    return null;
  }
  return solo;
};

export const getAvailableCabinsForWeekAndCategory = (
  allCabins,
  destinations,
  location,
  packageId,
  selectedWeek
) => {
  const cabins = getAvailableCabinsForWeek(
    allCabins,
    destinations,
    location,
    selectedWeek
  );
  const { category } = getSelectedParams(location);
  const finalCategory = packageId || category;
  if (!cabins || !finalCategory || !cabins[finalCategory]) {
    return null;
  }
  return cabins[parseInt(finalCategory, 10)];
};

export const getSelectedCabins = (allCabins, destinations, location, selectedWeek = null, packageId = null) => {
  const cabinsForCategory = getAvailableCabinsForWeekAndCategory(
    allCabins,
    destinations,
    location,
    packageId,
    selectedWeek
  );
  const { cabinType, soloType } = getSelectedParams(location);
  if (!cabinsForCategory) {
    return null;
  }
  const selectedCabins = [];

  cabinType.forEach(genderType => {
    const gender = genderType.split('Solo')[0];
    const isSoloCabin = genderType.split('Solo').length > 1;
    const persons = isSoloCabin ? 1 : 2;
    const matchingCabinWeek = cabinsForCategory?.cabin?.find(
      c => c.type === gender && c.persons === persons
    );
    if (matchingCabinWeek) {
      selectedCabins.push({
        ...matchingCabinWeek,
        genderType
      });
    }
  });
  soloType.forEach(genderType => {
    const matchingCabinWeek = cabinsForCategory?.solo?.find(c => c.type === genderType);
    if (matchingCabinWeek) {
      selectedCabins.push({
        ...matchingCabinWeek,
        genderType
      });
    }
  });

  return selectedCabins;
};

export const getCrewFromSelectedCabins = (
  allCabins,
  destinations,
  location,
  selectedWeek = null,
  packageId = null,
  bookedCabinWeeks
) => {
  const selectedCabins = bookedCabinWeeks || getSelectedCabins(allCabins, destinations, location, selectedWeek, packageId);
  if (!selectedCabins || selectedCabins?.length < 1) {
    return {
      boys: 0,
      girls: 0,
      total: 0,
      cabins: 0,
      solos: 0
    };
  }
  const mixedCabinsCrew = selectedCabins.filter(
    cabin => cabin.type === 'mixed'
  ).length;
  const boys =
    selectedCabins
      .filter(cabin => cabin.type === 'boy')
      .reduce(
        (acc, cabin) =>
          acc + (cabin.cabinType === 'cabin' && cabin.persons === 2 ? 2 : 1),
        0
      ) + mixedCabinsCrew;
  const girls =
    selectedCabins
      .filter(cabin => cabin.type === 'girl')
      .reduce(
        (acc, cabin) =>
          acc + (cabin.cabinType === 'cabin' && cabin.persons === 2 ? 2 : 1),
        0
      ) + mixedCabinsCrew;
  const cabins = selectedCabins.filter(
    cabin => cabin.cabinType === 'cabin'
  ).length;
  const solos = selectedCabins.filter(cabin => cabin.cabinType === 'solo').length;
  const total = boys + girls;
  return {
    boys,
    girls,
    total,
    cabins,
    solos
  };
};

export const getTotalPriceFromSelectedCabins = (
  allCabins,
  destinations,
  location,
  week = null,
  packageId = null
) => {
  const selectedWeek = getSelectedWeeks(destinations, location) || week;
  const selectedCabins = getSelectedCabins(allCabins, destinations, location, week, packageId);
  // getSelectedExtrasCost accepts as crew object of boys and girls so doesn't metter for counting where I add all...
  const crew = {
    girls: selectedCabins?.reduce((prev, curr) => prev + curr.persons , 0) || 0,
    boys: 0
  };
  const selectedExtrasCost = getSelectedExtrasCost({ week: selectedWeek[0] }, crew, true);
  const total =
    selectedCabins && selectedCabins.reduce((acc, item) => acc + item.price, 0);
  return total + selectedExtrasCost;
};

export const getSelectedStandardYachtWeek = (
  destinations,
  location,
  selectedWeekRow
) => {
  const selectedWeek =
    selectedWeekRow || getSelectedWeeks(destinations, location);
  const { category: packageId } = getSelectedParams(location);
  if ((selectedWeek && !selectedWeek.length) || !packageId) return null;
  const selectedStandardYachtWeek = selectedWeek[0].standardYachtWeeks.find(
    item => item.standardYacht.package.id === packageId
  );
  if (!selectedStandardYachtWeek) return null;
  const images =
    selectedWeek[0].destination.standardYachts.find(
      item => item.package.id === packageId
    ).images || [];
  selectedStandardYachtWeek.standardYacht.images = images;
  return selectedStandardYachtWeek;
};

export const isHostessIncluded = (destinations, location) => {
  const selectedStandardYachtWeek = getSelectedStandardYachtWeek(destinations, location);
  // selecting bvi destination because we dont have standartYachtWeeks applied to them so it saves us totally redesigning the getSelectedStandardYachtWeek() function;
  if (selectedStandardYachtWeek && selectedStandardYachtWeek.standardYacht.host) {
    return true;
  }
  // for now they decided new packages you will always have to choose host if you want
  return false;
  // if (location.pathname.includes('/browse/yachts') && !location.pathname.includes('/flotilla')) {
  //   return false;
  // }
  // const selectedStandardYachtWeek = getSelectedStandardYachtWeek(destinations, location);
  // if (selectedStandardYachtWeek) {
  //   return selectedStandardYachtWeek.standardYacht.host;
  // }
  // return false;
};

export const getSelectedCrew = (destinations, location) => {
  let { hostess = 0 } = getSelectedParams(location);
  let { boys = 4, girls = 4 } = getSelectedParams(location);
  // if for some reason is in query boys and girls = 0
  if (+boys === 0 && +girls === 0) {
    boys = 4;
    girls = 4;
  }
  hostess = hostess > 1 ? 0 : hostess;
  if (isHostessIncluded(destinations, location)) {
    hostess = 1;
  }
  const crew = {
    boys: parseInt(boys, 10),
    girls: parseInt(girls, 10),
    hostess: parseInt(hostess, 10),
    skipper: 1
  };
  crew.total = Object.keys(crew).reduce((acc, value) => acc + crew[value], 0);
  crew.ratio = calculateRatio(crew);
  return crew;
};

export const getSelectedStandardYachtFromDestination = (
  destinations,
  location
) => {
  const selectedDestination = getSelectedDestination(destinations, location);
  const { category: packageId } = getSelectedParams(location);
  if (!packageId || !selectedDestination) return null;
  const filtered = selectedDestination.standardYachts.filter(
    standardYacht => standardYacht.package.id === packageId
  );
  return filtered.length ? filtered[0] : null;
};

export const getSelectedPackageData = (destinations, location) => {
  const { category: packageId } = getSelectedParams(location);
  if (!packageId) return null;
  const selectedStandardYacht = getSelectedStandardYachtFromDestination(
    destinations,
    location
  );
  return selectedStandardYacht?.package;
};

export const getUniqueActivePackages = destinations => {
  const packagesDataObject = destinations.reduce((prev, curr) => {
    curr.standardYachts.forEach(sy => {
      prev[sy.package.id] = sy.package;
    });
    return prev;
  }, {});
  return packagesDataObject;
};

export const getUniqueActivePackagesForDestination = destination => {
  const packagesDataObject = destination.standardYachts.reduce((prev, curr) => {
    prev[curr.package.id] = curr.package;
    return prev;
  }, {});
  return packagesDataObject;
};

export const getPackageDataFromPackageId = (destinations, packageId) => {
  const packagesDataObject = getUniqueActivePackages(destinations);
  return packagesDataObject[packageId];
};

export const getAvailableLimitsByWeekAndCategory = (
  destinations,
  location,
  packageId,
  week
) => {
  const { category } = getSelectedParams(location);
  const finalCategory = packageId || category;
  const weeks = week ? [week] : getSelectedWeeks(destinations, location);
  if (!weeks.length || !finalCategory) {
    return null;
  }
  return weeks[0].available[finalCategory];
};

export const getYachtAndCabinStatusVariables = (
  destinations,
  location,
  allCabins,
  standardYachtWeek,
  hasAvailableCabinsForWeekAndCategory,
  packageId,
  selectedWeek
) => {
  const crew = getSelectedCrew(destinations, location);
  const weeks = selectedWeek ? [selectedWeek] : getSelectedWeeks(destinations, location); // we can pass selectedWeeks manually
  const minYachtPrice = standardYachtWeek ? getLeadPackagePricePerPerson(standardYachtWeek, weeks[0], crew) : null;
  const minCabinPrice = getCabinPriceOnWeekPerPerson(
    allCabins,
    destinations,
    location,
    packageId ||
      (standardYachtWeek && standardYachtWeek.standardYacht.package.id),
    weeks[0]
  );
  const isCabinsUnavailable =
    weeks[0].cabinStatus === WEEK.CLOSED_STATUS ||
    !hasAvailableCabinsForWeekAndCategory;
  let isCabinsSoldOut = false;
  let isCabinsComingSoon = false;
  const limits = getAvailableLimitsByWeekAndCategory(
    destinations,
    location,
    packageId,
    weeks[0]
  );
  if (!isCabinsUnavailable) {
    isCabinsComingSoon = weeks[0].cabinStatus === WEEK.COMING_SOON_STATUS;
    if (!isCabinsComingSoon) {
      const cabinsOnWeek = getAvailableCabinsForWeekAndCategory(
        allCabins,
        destinations,
        location,
        packageId,
        weeks[0]
      );
      let solosAvailable = true;
      let cabinsAvailable = true;
      if (cabinsOnWeek && limits) {
        solosAvailable =
          !!cabinsOnWeek.solo &&
          (limits[CABIN.SOLO_TYPE] === null || limits[CABIN.SOLO_TYPE] > 0);
        cabinsAvailable =
          !!cabinsOnWeek.cabin &&
          (limits[CABIN.CABIN_TYPE] === null || limits[CABIN.CABIN_TYPE] > 0);
      }
      const cabinsOrSolosAvailable = solosAvailable || cabinsAvailable;
      isCabinsSoldOut =
        weeks[0].cabinStatus === WEEK.SOLD_OUT_STATUS ||
        !minCabinPrice ||
        !cabinsOrSolosAvailable;
    }
  }
  const isYachtsUnavailable =
    (standardYachtWeek && standardYachtWeek.status === WEEK.CLOSED_STATUS) ||
    !standardYachtWeek ||
    weeks[0].status === WEEK.CLOSED_STATUS;
  let isYachtsSoldOut = false;
  let isYachtsComingSoon = false;
  if (!isYachtsUnavailable) {
    isYachtsComingSoon =
      weeks[0].status === WEEK.COMING_SOON_STATUS ||
      (standardYachtWeek && standardYachtWeek.status) ===
        WEEK.COMING_SOON_STATUS;
    if (!isYachtsComingSoon) {
      let yachtPackagesAvailable = true;
      if (limits) {
        yachtPackagesAvailable =
          limits.standard_yacht === null || limits.standard_yacht > 0;
      }
      isYachtsSoldOut =
        (standardYachtWeek &&
          standardYachtWeek.status === WEEK.SOLD_OUT_STATUS) ||
        !minYachtPrice ||
        !yachtPackagesAvailable ||
        weeks[0].status === WEEK.SOLD_OUT_STATUS;
    }
  }

  let isYachtsOnly =
    (!isYachtsSoldOut && isCabinsSoldOut) ||
    (isCabinsUnavailable && !isYachtsUnavailable) ||
    (!isYachtsSoldOut && isCabinsUnavailable) ||
    (!isYachtsSoldOut && isCabinsComingSoon);
  let isCabinsOnly =
    (isYachtsSoldOut && !isCabinsSoldOut) ||
    (!isCabinsUnavailable && isYachtsUnavailable) ||
    (isYachtsUnavailable && !isCabinsSoldOut) ||
    (isYachtsComingSoon && !isCabinsSoldOut);
  isYachtsOnly = isYachtsUnavailable ? false : isYachtsOnly;
  isCabinsOnly = isCabinsUnavailable ? false : isCabinsOnly;
  const cabinsHighDemandLimit = !isCabinsUnavailable && !isCabinsComingSoon && limits && limits.cabin != null && limits.cabin < 4 ? limits.cabin : null;
  const yachtsHighDemandLimit = !isYachtsUnavailable && !isYachtsSoldOut && !isYachtsComingSoon && limits && limits.standard_yacht != null && limits.standard_yacht < 4 && limits.standard_yacht > 0 ? limits.standard_yacht : null;
  return {
    isCabinsUnavailable,
    isCabinsSoldOut,
    isCabinsComingSoon,
    isYachtsUnavailable,
    isYachtsSoldOut,
    isYachtsComingSoon,
    isYachtsOnly,
    isCabinsOnly,
    cabinsHighDemandLimit,
    yachtsHighDemandLimit
  };
};

export const isExtraForCorrectSubject = (extra, isForCabins) => {
  let isForCorrectSubject = true;
  if (extra.yachtsOnlyExtra || extra.cabinsOnlyExtra) {
    isForCorrectSubject = isForCabins ? extra.cabinsOnlyExtra : extra.yachtsOnlyExtra;
  }
  return isForCorrectSubject;
};

export const getExtrasBySubject = (
  mandatoryExtras,
  isForCabins = false
) => {
  if (!mandatoryExtras) return [];
  return mandatoryExtras.filter(extra =>
    isExtraForCorrectSubject(extra, isForCabins));
};

export const getPrivateCharterWeeksForSelectedDestination = (
  destinations,
  location,
  yachts
) => {
  const selectedDestination = getSelectedDestination(destinations, location);
  const weeks =
    selectedDestination &&
    selectedDestination.weeks
      .sort((a, b) => new Date(a.beginDate) - new Date(b.beginDate))
      .filter(
        week =>
          (week.routeCode === ROUTES.PRIVATE_CHARTER_ROUTE ||
            !week.routeCode) &&
          week.status === WEEK.OPEN_STATUS &&
          yachts.some(yacht => yacht.week.id === week.id)
      );
  return weeks;
};

export const isPrivateCharterWeek = week =>
  week.routeCode === ROUTES.PRIVATE_CHARTER_ROUTE || !week.routeCode;
export const isSignaturerWeek = week =>
  week.routeCode === ROUTES.SIGNATURE_ROUTE;
export const isClassicWeek = week => week.routeCode === ROUTES.CLASSIC_ROUTE;

export const hasPrivateCharterWeeks = (destination, yachts = []) =>
  destination.weeks &&
  destination.weeks.some(
    week =>
      (week.routeCode === ROUTES.PRIVATE_CHARTER_ROUTE || !week.routeCode) &&
      week.status === WEEK.OPEN_STATUS &&
      yachts.some(yacht => yacht.week.id === week.id)
  );
export const hasSignatureWeeks = destination =>
  destination.weeks &&
  destination.weeks.some(
    week =>
      week.routeCode === ROUTES.SIGNATURE_ROUTE && !isWeekInvisible(week)
  );
export const hasClassicWeeks = destination =>
  destination.weeks &&
  destination.weeks.some(
    week => week.routeCode === ROUTES.CLASSIC_ROUTE && !isWeekInvisible(week)
  );

export const isYachtsAndFriends = week =>
  week && week.company.id === COMPANIES.YNF;
export const isSaltEscapes = (destinations, location) => {
  const selectedDestination = getSelectedDestination(destinations, location);
  return (
    selectedDestination &&
    selectedDestination.id === TYW_DESTINATION.YNF_SALT_ESCAPES_BVI
  );
};

export const signupModalText = (destinationId, isYachtsAndFriends) => {
  if (isYachtsAndFriends) {
    return 'Ready to join us for an adventure like no other at sea? Sign up now for early access to the best weeks and the best yachts at the best prices before they sell out. Bookings open on 4th March!';
  }
  if (isHideoutWeek(destinationId)) {
    return "Yacht Week x Hideout Festival. Seize the opportunity to explore the wonders of North Croatia and experience parties beyond your wildest dreams. Sign up now and let us know you're interested. Bookings open 28th October at 4PM UK time.";
  }
  if (destinationId === TYW_DESTINATION.BVI) {
    return "Ready to have the best New Year ever? If you don't have a pre-booking code, our general access opens on 18th April. Sign up here to be reminded when it does.";
  }
  return 'Ready to join us for an adventure like no other at sea? Sign up now for early access to the best weeks and the best yachts at the best prices before they sell out.';
};

export const getCrossSellDestinationByWeek = (
  week,
  location,
  history,
  pathname,
  filterYachts,
  destinations,
  cabins,
  forceCheckSameDestination
) => {
  // if selected week is from ignored array
  if (IGNORE_CROSS_SELL_DESTINATION_IDS.includes(week?.destination?.id)) {
    return [];
  }
  // 1. got one cross sell week from each destination based on selected week
  return (
    destinations
      .filter(d => !IGNORE_CROSS_SELL_DESTINATION_IDS.includes(d.id)) // filter out from cross-sell options ones from ignored array
      .map(d =>
        d.weeks.find(
          w =>
            w.week === week.week &&
            w.id !== week.id &&
            w.year === week.year &&
            !w.invisible &&
            (w.status === WEEK.OPEN_STATUS ||
              w.status === WEEK.PREBOOKING_STATUS) &&
            (forceCheckSameDestination ? week.destination.country === w.destination.country : true)
        ))
      .filter(b => b !== undefined)
      // 2. add goTo function for each week
      .map(crossWeek => ({
        ...crossWeek,
        fromPrice: calculateMinFromPriceOnWeek(crossWeek, cabins) / 100, // legacy. WeekRowButton works with cents...
        goToWeek: () =>
          onWeekSelection(
            crossWeek,
            crossWeek.destination,
            location,
            history,
            pathname,
            filterYachts,
            destinations
          )
      }))
  );
};

// Get available ratings for cabins
export const getAvailableCabinsPackages = (
  allCabins,
  destinations,
  location,
  selectedWeek
) => {
  const availableCabins = getAvailableCabinsForWeek(
    allCabins,
    destinations,
    location,
    selectedWeek
  );
  if (
    [WEEK.SOLD_OUT_STATUS, WEEK.COMING_SOON_STATUS].includes(
      selectedWeek.cabinStatus
    )
  ) {
    return [];
  }
  return availableCabins ? Object.keys(availableCabins).map(packageId => parseInt(packageId, 10)) : [];
};

// Get available packages for yachts
export const getAvailableYachtPackages = selectedWeek =>
  (selectedWeek.standardYachtWeeks.length &&
    selectedWeek.standardYachtWeeks
      .filter(w => w.status === WEEK.OPEN_STATUS)
      .map(w => w.standardYacht.package.id)) ||
  [];

export const getAvailableCategoriesPerCabinType = (
  availableCategories,
  selectedType
) =>
  Object.fromEntries(
    Object.entries(availableCategories).filter(
      ([packageId, data]) => {
      // Exclude same packageId
        if (+packageId === +selectedType.packageId) {
          return false;
        }
        const matchingCabinWeek = data[selectedType.cabinType]
          ?.find(
            c =>
              c.persons === selectedType.data.persons &&
          c.type === selectedType.data.type &&
          selectedType.data.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_ACTIVE &&
          selectedType.data.status === WEEK.OPEN_STATUS
          );
        return !!matchingCabinWeek;
      }
    )
  );


// Cross Availability ratings between cabins and yachts
export const getAvailableRating = (
  allCabins,
  destinations,
  location,
  selectedWeek
) => [
  ...new Set([
    ...getAvailableCabinsPackages(
      allCabins,
      destinations,
      location,
      selectedWeek
    ),
    ...getAvailableYachtPackages(selectedWeek)
  ])
];

export const isCategorySoldOut = (
  allCabins,
  destinations,
  location,
  selectedWeek,
  packageId
) => {
  const availableRating = getAvailableRating(
    allCabins,
    destinations,
    location,
    selectedWeek
  );
  const availableDataByRating = selectedWeek.available[packageId];
  const soloSpots = getAvailableSoloForWeekAndCategory(
    allCabins,
    destinations,
    location,
    packageId,
    selectedWeek
  );
  // SoldOut based on status of the week
  if (
    selectedWeek.status === WEEK.SOLD_OUT_STATUS ||
    selectedWeek.status === WEEK.COMING_SOON_STATUS ||
    !availableRating.includes(packageId)
  ) {
    return true;
  }

  // SoldOut based on availability count
  if (
    (availableDataByRating.standard_yacht === 0 ||
      selectedWeek.standardYachtWeeks.some(
        r =>
          r.standardYacht.package.id === packageId &&
          r.status === WEEK.SOLD_OUT_STATUS
      )) &&
    availableDataByRating.cabin === 0 &&
    (availableDataByRating.solo === 0 || !soloSpots)
  ) {
    return true;
  }

  return false;
};


export const getAvailabilityTextByRatingAndType = (
  allCabins,
  destinations,
  location,
  packageId,
  selectedWeek,
  type
) => {
  const soloSpots = getAvailableSoloForWeekAndCategory(
    allCabins,
    destinations,
    location,
    packageId,
    selectedWeek
  );
  const availableCabinsRatings = getAvailableCabinsPackages(
    allCabins,
    destinations,
    location,
    selectedWeek
  );
  const availableYachtRatings = getAvailableYachtPackages(selectedWeek);
  const availableDataByRating = selectedWeek.available[packageId];
  let text = '-';
  switch (type) {
  case 'yacht':
    // Text for standard yacht, if not set assume unlimited availability
    if (availableYachtRatings.includes(packageId)) {
      switch (availableDataByRating.standard_yacht) {
      case null:
        text = '3+ Yachts';
        break;
      case 0:
        text = '-';
        break;
      default:
        text =
              availableDataByRating.standard_yacht > 3 ? '3+ Yachts' : `${availableDataByRating.standard_yacht}  Yachts`;
      }
    }
    break;

  case 'cabin':
    // Text for cabin, if not set assume unlimited availability
    if (
      availableCabinsRatings.includes(packageId) &&
        !(selectedWeek.cabinStatus === WEEK.SOLD_OUT_STATUS)
    ) {
      switch (availableDataByRating.cabin) {
      case null:
        text = '3+ Cabins';
        break;
      case 0:
        text = '-';
        break;
      default:
        text =
              availableDataByRating.cabin > 3 ? '3+ Cabins' : `${availableDataByRating.cabin} Cabins`;
      }
    }
    break;

  case 'solo':
    // Text for solo, if not set assume unlimited availability as long we have solo spots in cabins
    if (
      availableCabinsRatings.includes(packageId) &&
        soloSpots &&
        !(selectedWeek.cabinStatus === WEEK.SOLD_OUT_STATUS)
    ) {
      switch (availableDataByRating.solo) {
      case null:
        text = '3+ Solos';
        break;
      case 0:
        text = '-';
        break;
      default:
        text =
              availableDataByRating.solo > 3 ? '3+ Solos' : `${availableDataByRating.solo}  Solos`;
      }
    }
    break;

  default:
    text = '-';
  }

  return text;
};

// Create an Obj containing availability data (text, color, rating) used on the Puck component and Availability Dropdown
export const getAvailabilityDataObj = (
  allCabins,
  destinations,
  location,
  selectedWeek
) => {
  const puckData = {};
  const destinationPackages = selectedWeek.destination.standardYachts.map(
    y => y.package
  );

  destinationPackages.forEach(destinationPackage => {
    const color = getPackageColorVariable(destinationPackage.id);
    const isSoldOut = isCategorySoldOut(
      allCabins,
      destinations,
      location,
      selectedWeek,
      destinationPackage.id
    );
    const text = {
      yacht: false,
      cabin: false,
      solo: false
    };

    // If week is not soldout create text
    if (!isSoldOut) {
      Object.keys(text).forEach(
        type =>
          (text[type] = getAvailabilityTextByRatingAndType(
            allCabins,
            destinations,
            location,
            destinationPackage.id,
            selectedWeek,
            type
          ))
      );
    }

    puckData[destinationPackage.id] = {
      package: destinationPackage,
      text,
      color,
      isSoldOut
    };
  });
  return puckData;
};

export const puckTooltip = puck => (
  <div className="text-left">
    <p className="text-md">{capitalizeFirstLetter(puck.package.name)}</p>
    {puck.text.yacht !== '-' && (
      <p className="text-sm">{puck.text.yacht} Available</p>
    )}
    {puck.text.cabin !== '-' && (
      <p className="text-sm">{puck.text.cabin} Available</p>
    )}
    {puck.text.solo !== '-' && (
      <p className="text-sm">{puck.text.solo} Available</p>
    )}
  </div>
);

export const hasLuxuryPlus = week => {
  week.standardYachtWeeks.forEach(yacht => {
    if (yacht.standardYacht.package.id === PACKAGES.LUXURY_PLUS) return true;
  });
  return false;
};

export const getCrossSellDestinations = (crossSell, cabinData, selectedType) =>
  crossSell.filter(crossSellWeek => {
    const categoryAvailability =
      cabinData[crossSellWeek.destination.id]?.[crossSellWeek.year]?.[
        crossSellWeek.week
      ]?.[selectedType.packageId];
    // If no availability or the week is sold_out
    if (
      !categoryAvailability ||
      crossSellWeek.cabinStatus === WEEK.SOLD_OUT_STATUS
    ) {
      return false;
    }
    // If ignored cross-sell
    if (
      IGNORE_CROSS_SELL_DESTINATION_IDS.includes(crossSellWeek?.destination?.id)
    ) {
      return false;
    }
    const matchingCrossSellCabinType = categoryAvailability[selectedType.cabinType]
      ?.find(
        c =>
          c.persons === selectedType.data.persons &&
        c.type === selectedType.data.type &&
        selectedType.data.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_ACTIVE &&
        selectedType.data.status === WEEK.OPEN_STATUS
      );

    if (matchingCrossSellCabinType && crossSellWeek.available[selectedType.packageId][selectedType.data.cabinType] > 0) {
      crossSellWeek.cabinPrice = matchingCrossSellCabinType.price / matchingCrossSellCabinType.persons;
      return true;
    }
    return false;
  });

export const isSelectedCabinsSoldOut = (allCabins, destinations, location, week = null) => {
  const selectedCabins = getSelectedCabins(allCabins.data, destinations, location) || [];
  if (selectedCabins.some(c => c.cabinWeekStatus === CABIN_WEEK_STATUS.STATUS_SOLD_OUT)) {
    return true;
  }
  if (week) {
    const limits = getAvailableLimitsByWeekAndCategory(destinations, location, null, week);
    if (limits) {
      const hasSelectedCabins = selectedCabins.some(c => c.cabinType === CABIN.CABIN_TYPE);
      const hasSelectedSolos = selectedCabins.some(c => c.cabinType === CABIN.SOLO_TYPE);
      if (hasSelectedCabins && limits.cabin === 0) return true;
      if (hasSelectedSolos && limits.solo === 0) return true;
    }
  }
  return false;
};

export const cleanSelectedExtras = destinations => {
  // check if selected extras are available on selected week otherwise remove them from query
  const { extras } = getSelectedParams(location);
  const selectedWeeks = getSelectedWeeks(destinations, location);
  const selectedWeek = selectedWeeks?.length && selectedWeeks[0];
  if (selectedWeek && extras.length) {
    const isCabin = history.location.pathname.indexOf('/cabins') > -1;
    const availableNonVariableExtrasIds = getExtrasBySubject(selectedWeek.nonVariableExtras, isCabin).map(e => e.id);
    const cleanedExtras = [];
    extras.forEach(id => availableNonVariableExtrasIds.includes(id) && cleanedExtras.push(id));
    history.replace({
      pathname: history.location.pathname,
      search: updateSearch(history.location, {
        extras: cleanedExtras
      })
    });
  }
};

/**
 * Festival branding for a week, in the day8-ui FeaturedWeekConstant shape, or null for an original week.
 *
 * The day8-ui FEATURED_WEEKS entry wins while it exists (festival weeks still live inside Croatia and
 * Greece Original); otherwise it comes from the week's destination, whichever payload the week came from:
 * yacht weeks carry `destinationFeatured`, destination-tree and booking weeks carry `destination.featured`.
 * When FEATURED_WEEKS goes, only this function changes.
 *
 * @param {object|number|string} week week object, or a bare week id (constant lookup only)
 * @param {object} [destination] the week's destination when the week object does not carry it
 */
export const getFeaturedData = (week, destination = null) => {
  const weekId = week && typeof week === 'object' ? week.id : week;
  if (weekId != null && FEATURED_WEEKS[+weekId]) {
    return FEATURED_WEEKS[+weekId];
  }
  if (week && typeof week === 'object') {
    if (week.destinationFeatured) {
      return week.destinationFeatured;
    }
    if (week.destination && typeof week.destination === 'object' && week.destination.featured) {
      return week.destination.featured;
    }
  }
  return destination?.featured || null;
};

export const isFeaturedWeek = (week, destination = null) => !!getFeaturedData(week, destination);

export const getFeaturedWeekPillData = (week, isWeekPill, destination = null) => {
  const featured = getFeaturedData(week, destination);
  return featured?.[isWeekPill ? 'weekPill' : 'featuredPill'] || null;
};
export const getFeaturedWeekData = (week, destination = null) => getFeaturedData(week, destination);
export const getConfirmEventName = (country, route, week) => {
  const pillData = getFeaturedWeekPillData(week);
  let text = country;
  if (pillData?.title || route) {
    text += `, ${pillData?.title || route}`;
  }
  return text;
};

/**
 * Klaviyo list a week's sign-up and "notify me" forms subscribe to: a festival week's own (FEATURED_WEEKS while
 * it exists), else its destination's (destinations.address_book_id, set per destination in admin).
 *
 * @param {object} week
 * @param {object} [destination] the week's destination when the week does not carry it (yacht weeks)
 * @returns {string|null}
 */
export const getAddressBookId = (week, destination = null) => getFeaturedData(week, destination)?.addressBookId
  || destination?.addressBookId
  || (week && typeof week.destination === 'object' ? week.destination?.addressBookId : null)
  || null;

export const getEmailSignupData = (week, img, destinationName, destinationId, destination = null) => {
  const featured = getFeaturedData(week, destination);
  return {
    title: "Coming soon",
    text: "Early bookers get rewarded. Sign up to get early access to the best yachts and best deals at booking opening.",
    image: {
      url: featured?.image?.url || img || 'https://wpassets.theyachtweek.com/wp-content/uploads/2020/08/TYW-Afternoon-1-scaled.jpg',
      alt: destinationName
    },
    ignoreCookie: true,
    addressBookId: getAddressBookId(week, destination) || constants.ADDRESS_BOOKS['tyw-footer']
  };
};

export const getWaitingListData = (weekNumber, destinationName, packageName, extra = 'not set') => {
  return {
    week: weekNumber,
    destination: destinationName,
    text: 'Yachts are sold out for this week. Join the waitlist and we’ll let you know if spaces become available.',
    extra,
    packageFilter: packageName
  };
};